import { Link } from "react-router";
import { ArrowRight, CheckCircle2, ClipboardCheck, ShieldCheck, Video } from "lucide-react";
import { useEffect, useMemo } from "react";

import { RefreshButton } from "@/components/admin/refresh-button";
import { SessionActivityCard } from "@/components/admin/session-activity-card";
import { useDoctorReview } from "@/components/admin/use-doctor-review";
import { EmptyState } from "@/components/common/empty-state";
import { StatCard } from "@/components/common/stat-card";
import { DoctorStatusBadge } from "@/components/common/status-badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { useAdminUi } from "@/contexts/AdminUiContext";
import { usePendingDoctors } from "@/hooks/admin/use-admin-doctors";
import { formatDate, initials } from "@/lib/format";
import { usePageMeta } from "@/lib/page-meta";

export function AdminDashboardRoute() {
  usePageMeta({
    title: "Admin Dashboard — HealNova Admin Portal",
    description:
      "Review pending HealNova doctor registrations, track approvals and rejections from the internal admin dashboard.",
    robots: "noindex",
  });
  return <AdminDashboardPage />;
}

function AdminDashboardPage() {
  const { doctors, isLoading, refresh, refreshing } = usePendingDoctors();
  const { activity } = useAdminAuth();
  const { registerRefresh } = useAdminUi();
  const { openDoctor, askApprove, askReject, dialogs } = useDoctorReview();

  useEffect(() => {
    registerRefresh(refresh);
    return () => registerRefresh(null);
  }, [registerRefresh, refresh]);

  const stats = useMemo(() => {
    const approved = activity.filter((item) => item.kind === "approved").length;
    const rejected = activity.length - approved;
    const video = doctors.filter((doctor) => doctor.videoConsultationAvailable).length;
    return { approved, rejected, video };
  }, [activity, doctors]);

  const recent = doctors.slice(0, 5);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
            <ShieldCheck className="size-3.5" aria-hidden="true" /> Administration
          </span>
          <h1 className="mt-1 font-display text-3xl font-semibold text-balance">
            Doctor approvals overview
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Review new HealNova doctor registrations and decide who goes live in the patient app.
          </p>
        </div>
        <RefreshButton onRefresh={() => void refresh()} refreshing={refreshing} />
      </header>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Pending review"
          value={doctors.length}
          icon={ClipboardCheck}
          accent="primary"
          loading={isLoading}
          hint="Awaiting an approval decision"
        />
        <StatCard
          label="Approved this session"
          value={stats.approved}
          icon={CheckCircle2}
          accent="success"
          hint="Now visible to patients"
        />
        <StatCard
          label="Rejected this session"
          value={stats.rejected}
          icon={ShieldCheck}
          accent="rose"
          hint="Hidden from the patient app"
        />
        <StatCard
          label="Video-ready pending"
          value={stats.video}
          icon={Video}
          accent="info"
          loading={isLoading}
          hint="Offer video consultations"
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.6fr_1fr]">
        <section className="card-surface flex flex-col">
          <header className="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
            <div>
              <h2 className="font-display text-base font-semibold">Latest registrations</h2>
              <p className="mt-0.5 text-xs text-muted-foreground">
                The five oldest doctors waiting for a decision.
              </p>
            </div>
            <Button variant="ghost" size="sm" asChild className="shrink-0">
              <Link to="/admin/doctors">
                View all <ArrowRight className="size-4" />
              </Link>
            </Button>
          </header>

          {isLoading ? (
            <ul className="divide-y divide-border">
              {Array.from({ length: 4 }).map((_, index) => (
                <li key={index} className="flex items-center gap-3 px-5 py-4">
                  <div className="size-10 animate-pulse rounded-full bg-muted" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3.5 w-40 animate-pulse rounded bg-muted" />
                    <div className="h-3 w-24 animate-pulse rounded bg-muted" />
                  </div>
                </li>
              ))}
            </ul>
          ) : recent.length === 0 ? (
            <EmptyState
              icon={CheckCircle2}
              title="You're all caught up"
              description="Every doctor registration has been reviewed. New submissions will appear here."
              action={<RefreshButton onRefresh={() => void refresh()} refreshing={refreshing} />}
            />
          ) : (
            <ul className="divide-y divide-border">
              {recent.map((doctor) => (
                <li key={doctor.id} className="flex flex-wrap items-center gap-3 px-5 py-4">
                  <button
                    type="button"
                    onClick={() => openDoctor(doctor)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  >
                    <Avatar className="size-10 border border-border">
                      {doctor.profilePicture && <AvatarImage src={doctor.profilePicture} alt="" />}
                      <AvatarFallback className="bg-primary text-xs font-semibold text-primary-foreground">
                        {initials(doctor.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{doctor.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {doctor.specialization} · {doctor.experience} yrs ·{" "}
                        {formatDate(doctor.createdAt)}
                      </span>
                    </span>
                  </button>
                  <DoctorStatusBadge status={doctor.status} />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-lg"
                      onClick={() => askReject(doctor)}
                    >
                      Reject
                    </Button>
                    <Button size="sm" className="rounded-lg" onClick={() => askApprove(doctor)}>
                      Approve
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <SessionActivityCard activity={activity} />
      </div>

      {dialogs}
    </div>
  );
}
